import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Toaster } from 'react-hot-toast';
import Header from './components/Header';
import LoginPage from './components/LoginPage';
import ConfigSection from './components/ConfigSection';
import WalletCard from './components/WalletCard';
import MarketCapWalletCard from './components/MarketCapWalletCard';
import SecurityWarning from './components/SecurityWarning';
import ShowLogsButton from './components/ShowLogsButton';
import StatisticsPanel from './components/StatisticsPanel';
import GasInfoPanel from './components/GasInfoPanel';
import MassTransferSection from './components/MassTransferSection';
import { GenesisDCAFormCard, GenesisPipelineList } from './components/GenesisDCASection';
import { ThemeToggle } from './components/ThemeToggle';
import { ThemeProvider } from './contexts/ThemeContext';
import { GlobalStyles } from './styles/GlobalStyles';
import { Wallet, Config, LogEntry, GenesisActivePipeline, GenesisPipelineListItem } from './types';
import { apiService } from './services/api';
import { generateId } from './utils/helpers';
import { useTransactionLogs } from './hooks/useTransactionLogs';
import { loadConfig, saveConfig } from './utils/configStorage';

const AppContainer = styled.div`
  min-height: 100vh;
  background: var(--bg-primary);
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease;
`;

const MainContent = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    padding: 0 10px;
  }
`;

const WalletsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
  margin-top: 20px;
  
  @media (min-width: 1800px) {
    grid-template-columns: repeat(5, 1fr);
  }
  
  @media (min-width: 1400px) and (max-width: 1799px) {
    grid-template-columns: repeat(4, 1fr);
  }
  
  @media (min-width: 1024px) and (max-width: 1399px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AddWalletButton = styled.button`
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.25);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.35);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const TabsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 24px 0;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  background: var(--bg-secondary);
  padding: 4px;
  border-radius: 12px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  background: ${props => props.active 
    ? 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-hover) 100%)' 
    : 'transparent'
  };
  color: ${props => props.active ? 'white' : 'var(--text-secondary)'};
  border: none;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 140px;
  
  ${props => props.active && `
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
  `}
  
  &:hover {
    background: ${props => props.active 
      ? 'linear-gradient(135deg, var(--accent-hover) 0%, var(--accent-primary) 100%)' 
      : 'var(--bg-tertiary)'
    };
    transform: ${props => props.active ? 'translateY(-1px)' : 'none'};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    min-width: unset;
  }
`;

type TabType = 'standard' | 'marketcap' | 'genesis';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!sessionStorage.getItem('auth_token'));

  if (!isAuthenticated) {
    return <LoginPage onLogin={() => setIsAuthenticated(true)} />;
  }

  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const [config, setConfig] = useState<Config | null>(null);
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [marketCapWallets, setMarketCapWallets] = useState<Wallet[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('standard');
  interface GenesisFormState {
    id: string;
    activePipelineId?: string;
    activePipelineStatus?: string;
    initialSettings?: GenesisActivePipeline;
  }
  const [genesisForms, setGenesisForms] = useState<GenesisFormState[]>([]);
  const [genesisRefreshTrigger, setGenesisRefreshTrigger] = useState(0);
  const [isLoadingActivePipelines, setIsLoadingActivePipelines] = useState(false);
  const [genesisPipelinesList, setGenesisPipelinesList] = useState<GenesisPipelineListItem[]>([]);
  const [isConfigured, setIsConfigured] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const walletsRef = useRef<Wallet[]>([]);
  const marketCapWalletsRef = useRef<Wallet[]>([]);
  const isUpdatingRef = useRef(false);
  
  // Хук для логов транзакций
  const {
    logs: transactionLogs,
    simulateBackendLogs,
  } = useTransactionLogs();

  useEffect(() => {
    // Загружаем сохранённую конфигурацию из localStorage (без приватных ключей)
    const stored = loadConfig();
    if (stored && stored.rpcUrl && stored.tokenAddress && stored.ethAddress) {
      setConfig({
        rpcUrl: stored.rpcUrl,
        tokenAddress: stored.tokenAddress,
        ethAddress: stored.ethAddress,
        tokenSymbol: stored.tokenSymbol,
        tokenDecimals: stored.tokenDecimals,
        chainId: stored.chainId,
        dexType: stored.dexType,
        selectedPoolId: stored.selectedPoolId,
        selectedPoolInfo: stored.selectedPoolInfo,
      });
      setIsConfigured(true);
    } else {
      setIsConfigured(false);
    }
  }, []);

  // Обновляем ref при изменении wallets
  useEffect(() => {
    walletsRef.current = wallets;
  }, [wallets]);

  useEffect(() => {
    marketCapWalletsRef.current = marketCapWallets;
  }, [marketCapWallets]);

  useEffect(() => {
    // Обновляем статус активных стратегий каждые 15 секунд
    const interval = setInterval(() => {
      const activeWallets = walletsRef.current.filter(wallet => 
        wallet.strategyId && wallet.strategyStatus === 'running'
      );
      const activeMarketCapWallets = marketCapWalletsRef.current.filter(wallet => 
        wallet.strategyId && (wallet.strategyStatus === 'running' || wallet.strategyStatus === 'waiting_marketcap')
      );
      
      if (activeWallets.length > 0 || activeMarketCapWallets.length > 0) {
        updateActiveStrategies();
      }
    }, 15000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Убираем зависимость от wallets

  // loadConfig удален — конфигурация хранится локально

  const handleConfigSave = useCallback((newConfig: Config) => {
    setConfig(prev => {
      // Avoid state updates if nothing changed
      if (prev &&
          prev.rpcUrl === newConfig.rpcUrl &&
          prev.tokenAddress === newConfig.tokenAddress &&
          prev.ethAddress === newConfig.ethAddress &&
          prev.dexType === newConfig.dexType &&
          prev.selectedPoolId === newConfig.selectedPoolId) {
        return prev;
      }
      return newConfig;
    });
    setIsConfigured(true);
    saveConfig(newConfig);
  }, []);

  const addWallet = () => {
    const newWallet: Wallet = {
      id: generateId(),
      privateKey: '',
      address: '',
      ethBalance: 0,
      tokenBalance: 0,
      isLoadingBalance: false,
    };
    
    if (activeTab === 'standard') {
      setWallets(prevWallets => [...prevWallets, newWallet]);
    } else {
      setMarketCapWallets(prevWallets => [...prevWallets, newWallet]);
    }
  };

  const addGenesisForm = () => {
    setGenesisForms(prev => [...prev, { id: generateId() }]);
  };

  const removeGenesisForm = (id: string) => {
    setGenesisForms(prev => prev.filter(f => f.id !== id));
  };

  const loadActivePipelines = async () => {
    setIsLoadingActivePipelines(true);
    try {
      const result = await apiService.getGenesisActivePipelines();
      if (result.active_pipelines.length === 0) {
        const toast = (await import('react-hot-toast')).default;
        toast('No active pipelines found');
        return;
      }
      const activeForms: GenesisFormState[] = result.active_pipelines.map(ap => ({
        id: generateId(),
        activePipelineId: ap.pipeline_id,
        activePipelineStatus: ap.status,
        initialSettings: ap,
      }));
      setGenesisForms(activeForms);
      setGenesisRefreshTrigger(prev => prev + 1);
    } catch (err: any) {
      const toast = (await import('react-hot-toast')).default;
      toast.error('Failed to load active pipelines');
    } finally {
      setIsLoadingActivePipelines(false);
    }
  };

  // Load genesis pipelines list for Copy From
  useEffect(() => {
    if (activeTab !== 'genesis') return;
    const load = async () => {
      try {
        const result = await apiService.listGenesisPipelines();
        setGenesisPipelinesList(result.pipelines);
      } catch { /* ignore */ }
    };
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [activeTab, genesisRefreshTrigger]);

  const updateWallet = useCallback((id: string, updates: Partial<Wallet>) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[App] Updating wallet ${id}:`, updates);
    }
    setWallets(prevWallets => 
      prevWallets.map(wallet => 
        wallet.id === id ? { ...wallet, ...updates } : wallet
      )
    );
  }, []);

  const updateMarketCapWallet = useCallback((id: string, updates: Partial<Wallet>) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[App] Updating market cap wallet ${id}:`, updates);
    }
    setMarketCapWallets(prevWallets => 
      prevWallets.map(wallet => 
        wallet.id === id ? { ...wallet, ...updates } : wallet
      )
    );
  }, []);

  const removeWallet = (id: string) => {
    setWallets(prevWallets => prevWallets.filter(wallet => wallet.id !== id));
  };

  const removeMarketCapWallet = (id: string) => {
    setMarketCapWallets(prevWallets => prevWallets.filter(wallet => wallet.id !== id));
  };

  const addLog = useCallback((level: LogEntry['level'], message: string, strategy?: string) => {
    const newLog: LogEntry = {
      id: generateId(),
      timestamp: new Date().toISOString(),
      level,
      message,
      strategy,
    };
    setLogs(prevLogs => [...prevLogs, newLog]);
  }, []);

  // Функция для обновления статуса активных стратегий
  const updateActiveStrategies = async () => {
    // Предотвращаем одновременные запросы
    if (isUpdatingRef.current) {
      return;
    }
    
    isUpdatingRef.current = true;
    
    try {
      const activeWallets = walletsRef.current.filter(wallet => 
        wallet.strategyId && wallet.strategyStatus === 'running'
      );
      const activeMarketCapWallets = marketCapWalletsRef.current.filter(wallet => 
        wallet.strategyId && (wallet.strategyStatus === 'running' || wallet.strategyStatus === 'waiting_marketcap')
      );

      // Обновляем обычные кошельки
      for (const wallet of activeWallets) {
      try {
        const status = await apiService.getTask(wallet.strategyId!);
        
        // Обновляем статус кошелька (только если есть изменения)
        const updates: Partial<Wallet> = {};
        
        if (status.status !== wallet.strategyStatus) {
          updates.strategyStatus = status.status as Wallet['strategyStatus'];
        }
        
        if (status.executed_transactions !== wallet.executed) {
          updates.executed = status.executed_transactions;
        }
        
        if (status.total_transactions !== wallet.total) {
          updates.total = status.total_transactions;
        }
        
        if (status.error_code !== wallet.errorCode) {
          updates.errorCode = status.error_code;
        }
        
        if (status.last_error !== wallet.errorMessage) {
          updates.errorMessage = status.last_error;
        }
        
        if (status.operations_count !== wallet.errorCount) {
          updates.errorCount = status.operations_count;
        }
        
        // Обновляем только если есть изменения
        if (Object.keys(updates).length > 0) {
          console.log(`[App] Status update for wallet ${wallet.id}:`, {
            oldStatus: wallet.strategyStatus,
            newStatus: updates.strategyStatus,
            oldExecuted: wallet.executed,
            newExecuted: updates.executed,
            updates
          });
          updateWallet(wallet.id, updates);
        }

        // Логируем изменения статуса
        if (status.status !== wallet.strategyStatus) {
          addLog('info', `Strategy ${wallet.strategyId} status changed to ${status.status}`, wallet.strategyId);
        }

        // Логируем прогресс транзакций
        if (status.executed_transactions !== wallet.executed) {
          addLog('success', `Strategy ${wallet.strategyId} executed transaction ${status.executed_transactions}/${status.total_transactions}`, wallet.strategyId);
          
          // Симулируем логи транзакции
          if (status.executed_transactions > (wallet.executed || 0)) {
            const strategyId = wallet.strategyId!;
            
            // Симулируем этапы транзакции
            setTimeout(() => {
              simulateBackendLogs(strategyId, 'swap_start', { 
                action: wallet.strategyAction || 'buy', 
                amount: '100.5' 
              });
            }, 100);
            
            setTimeout(() => {
              simulateBackendLogs(strategyId, 'swap_step', { 
                step: 1, 
                total: 7, 
                description: 'Получение информации о токене' 
              });
            }, 200);
            
            setTimeout(() => {
              simulateBackendLogs(strategyId, 'swap_success', { 
                message: 'Токен: VICE, decimals=18' 
              });
            }, 300);
            
            setTimeout(() => {
              simulateBackendLogs(strategyId, 'tx_success', {
                execution: status.executed_transactions,
                tx_hash: `0x${Math.random().toString(16).substr(2, 64)}`,
                explorer_url: `https://etherscan.io/tx/0x${Math.random().toString(16).substr(2, 64)}`,
                nonce: 19 + status.executed_transactions,
                gas_price: '1.03'
              });
            }, 400);
          }
        }

        } catch (error: any) {
          console.error(`Failed to update strategy ${wallet.strategyId}:`, error);
          // Не логируем ошибки обновления статуса, чтобы не засорять логи
        }
      }

      // Обновляем MarketCap кошельки
      for (const wallet of activeMarketCapWallets) {
        try {
          const status = await apiService.getTask(wallet.strategyId!);
          
          const updates: Partial<Wallet> = {};
          
          if (status.status !== wallet.strategyStatus) {
            updates.strategyStatus = status.status as Wallet['strategyStatus'];
          }
          
          if (status.operations_count !== wallet.executed) {
            updates.executed = status.operations_count;
          }
          
          if (status.error_code !== wallet.errorCode) {
            updates.errorCode = status.error_code;
          }
          
          if (status.last_error !== wallet.errorMessage) {
            updates.errorMessage = status.last_error;
          }
          
          if (Object.keys(updates).length > 0) {
            console.log(`[App] MarketCap Status update for wallet ${wallet.id}:`, {
              oldStatus: wallet.strategyStatus,
              newStatus: updates.strategyStatus,
              oldExecuted: wallet.executed,
              newExecuted: updates.executed,
              updates
            });
            updateMarketCapWallet(wallet.id, updates);
          }

          if (status.status !== wallet.strategyStatus) {
            addLog('info', `MarketCap Strategy ${wallet.strategyId} status changed to ${status.status}`, wallet.strategyId);
          }

          if (status.operations_count !== wallet.executed) {
            addLog('success', `MarketCap Strategy ${wallet.strategyId} executed operation ${status.operations_count}`, wallet.strategyId);
            
            if (status.operations_count > (wallet.executed || 0)) {
              const strategyId = wallet.strategyId!;
              
              setTimeout(() => {
                simulateBackendLogs(strategyId, 'swap_start', { 
                  action: wallet.strategyAction || 'buy', 
                  amount: '100.5' 
                });
              }, 100);
              
              setTimeout(() => {
                simulateBackendLogs(strategyId, 'swap_step', { 
                  step: 1, 
                  total: 7, 
                  description: 'Получение информации о токене' 
                });
              }, 200);
              
              setTimeout(() => {
                simulateBackendLogs(strategyId, 'swap_success', { 
                  message: 'Токен: VICE, decimals=18' 
                });
              }, 300);
              
              setTimeout(() => {
                simulateBackendLogs(strategyId, 'tx_success', {
                  execution: status.operations_count,
                  tx_hash: `0x${Math.random().toString(16).substr(2, 64)}`,
                  explorer_url: `https://etherscan.io/tx/0x${Math.random().toString(16).substr(2, 64)}`,
                  nonce: 19 + status.operations_count,
                  gas_price: '1.03'
                });
              }, 400);
            }
          }

        } catch (error: any) {
          console.error(`Failed to update MarketCap strategy ${wallet.strategyId}:`, error);
        }
      }
    } finally {
      isUpdatingRef.current = false;
    }
  };

  return (
    <ThemeProvider>
      <GlobalStyles />
      <AppContainer>
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
            },
          }}
        />
        
        <MainContent>
          <Header />
          
          <ThemeToggle />
          
          <SecurityWarning />
          <ShowLogsButton logs={[...logs, ...transactionLogs].sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          )} />
          
          <ConfigSection 
            config={config}
            isConfigured={isConfigured}
            onConfigSave={handleConfigSave}
          />

          <GasInfoPanel rpcUrl={config?.rpcUrl} network={'base'} />

          <TabsWrapper>
            <TabContainer>
              <Tab 
                active={activeTab === 'standard'} 
                onClick={() => setActiveTab('standard')}
              >
                ⚡ Standard DCA
              </Tab>
              {/* <Tab
                active={activeTab === 'marketcap'}
                onClick={() => setActiveTab('marketcap')}
              >
                📊 MarketCap DCA
              </Tab> */}
              <Tab
                active={activeTab === 'genesis'}
                onClick={() => setActiveTab('genesis')}
              >
                Genesis DCA
              </Tab>
            </TabContainer>

            {activeTab === 'genesis' ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              <AddWalletButton onClick={addGenesisForm}>
                <span>+</span> Add Genesis DCA Pipeline
              </AddWalletButton>
              <AddWalletButton onClick={loadActivePipelines} disabled={isLoadingActivePipelines}>
                {isLoadingActivePipelines ? 'Loading...' : 'Get Active DCA'}
              </AddWalletButton>
            </div>
            ) : (
            <AddWalletButton onClick={addWallet}>
              <span>+</span> Add Wallet
            </AddWalletButton>
            )}
          </TabsWrapper>

        {activeTab === 'standard' && (
        <WalletsContainer>
          {wallets.map((wallet, index) => {
            try {
              return (
                <WalletCard
                  key={wallet.id}
                  wallet={wallet}
                  walletNumber={index + 1}
                  config={config}
                  onUpdate={updateWallet}
                  onRemove={removeWallet}
                  onLog={addLog}
                  onTransactionLog={simulateBackendLogs}
                />
              );
            } catch (error) {
              console.error(`Error rendering wallet ${wallet.id}:`, error);
              return (
                <div key={wallet.id} style={{ 
                  background: '#fee2e2', 
                  border: '1px solid #fca5a5', 
                  borderRadius: '8px', 
                  padding: '16px',
                  color: '#991b1b'
                }}>
                  Error rendering wallet {index + 1}: {String(error)}
                </div>
              );
            }
          })}
        </WalletsContainer>
        )}

        {activeTab === 'marketcap' && (
        <WalletsContainer>
          {marketCapWallets.map((wallet, index) => {
            try {
              return (
                <MarketCapWalletCard
                  key={wallet.id}
                  wallet={wallet}
                  walletNumber={index + 1}
                  config={config}
                  onUpdate={updateMarketCapWallet}
                  onRemove={removeMarketCapWallet}
                  onLog={addLog}
                  onTransactionLog={simulateBackendLogs}
                />
              );
            } catch (error) {
              console.error(`Error rendering market cap wallet ${wallet.id}:`, error);
              return (
                <div key={wallet.id} style={{ 
                  background: '#fee2e2', 
                  border: '1px solid #fca5a5', 
                  borderRadius: '8px', 
                  padding: '16px',
                  color: '#991b1b'
                }}>
                  Error rendering market cap wallet {index + 1}: {String(error)}
                </div>
              );
            }
          })}
        </WalletsContainer>
        )}

        {activeTab === 'genesis' && (
          <>
            <WalletsContainer>
              {genesisForms.map((form, index) => (
                <GenesisDCAFormCard
                  key={form.id}
                  config={config}
                  formNumber={index + 1}
                  onRemove={() => removeGenesisForm(form.id)}
                  onPipelineStarted={(pipelineId: string) => {
                    setGenesisForms(prev => prev.map(f =>
                      f.id === form.id ? { ...f, activePipelineId: pipelineId, activePipelineStatus: 'starting' } : f
                    ));
                    setGenesisRefreshTrigger(prev => prev + 1);
                  }}
                  activePipelineId={form.activePipelineId}
                  activePipelineStatus={form.activePipelineStatus}
                  initialSettings={form.initialSettings}
                  allPipelines={genesisPipelinesList}
                  onStop={() => setGenesisRefreshTrigger(prev => prev + 1)}
                />
              ))}
            </WalletsContainer>
            <GenesisPipelineList refreshTrigger={genesisRefreshTrigger} />
          </>
        )}

        <MassTransferSection rpcUrl={config?.rpcUrl} />

        {activeTab !== 'genesis' && (
        <StatisticsPanel
          wallets={activeTab === 'standard' ? wallets : marketCapWallets}
          config={config}
          isConfigured={isConfigured}
        />
        )}
        </MainContent>
      </AppContainer>
    </ThemeProvider>
  );
}

export default App;
